import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronRight, Play, MapPin, Star, Award, CalendarCheck } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { useRef } from 'react';
import { BOX_IMAGES } from '@/data/images';
import { useHeroImage } from '@/hooks/useHeroImage';

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // Home page hero can be overridden via admin (pageKey = "/")
  const heroPortrait = useHeroImage('/', BOX_IMAGES.barbellPrep, 'Athlète CrossFit Saint-Chamond - préparation barbell');

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative bg-dark overflow-hidden"
    >
      {/* Subtle background image + dark gradient */}
      <motion.div className="absolute inset-0" style={{ scale: bgScale }}>
        <img decoding="async"
          src={BOX_IMAGES.groupBarbell}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-20"
          loading="eager"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/95 to-primary-dark/40" />
      <div className="absolute inset-0 mesh-gradient opacity-40 pointer-events-none" />

      {/* Accent blobs */}
      <div className="absolute -top-40 -right-20 w-[40vw] h-[40vw] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 w-[30vw] h-[30vw] bg-primary-glow/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-custom relative z-10 pt-20 pb-16 md:pt-28 md:pb-24 lg:pt-32 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* ===== LEFT: Copy ===== */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 bg-primary/15 border border-primary/30 rounded-full pl-2 pr-4 py-1.5 mb-6 backdrop-blur-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-glow opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-glow" />
              </span>
              <span className="text-white text-[11px] font-semibold tracking-[0.2em] uppercase">
                CrossFit Saint-Chamond · HYROX Training Club
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="mb-6"
            >
              <span className="block font-heading text-xl sm:text-2xl text-white/70 mb-3 tracking-[0.15em]">
                LE BIEN-ÊTRE
              </span>
              <span className="block font-display text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem] text-white leading-[0.85] tracking-tight">
                PAR LE
              </span>
              <span className="block font-display text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem] text-gradient-primary leading-[0.85] tracking-tight drop-shadow-[0_10px_40px_hsla(150,70%,45%,0.4)]">
                SPORT.
              </span>
              <span className="block font-heading text-base sm:text-lg text-white/55 mt-5 tracking-[0.12em] uppercase">
                À CrossFit Saint-Chamond · Loire (42)
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
              className="text-white/75 text-base md:text-lg max-w-xl mb-8 leading-relaxed"
            >
              Coaching sportif professionnel à Saint-Chamond. WOD, HYROX, Team
              WOD, Free Access. Accessible à tous, du débutant au confirmé.
              <span className="inline-flex items-center gap-1.5 mt-3 text-white/55 text-sm">
                <MapPin className="h-3.5 w-3.5 text-primary-glow" />
                7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond
              </span>
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
              className="flex flex-col sm:flex-row gap-4 mb-4"
            >
              <Link to="/contact" data-testid="hero-cta-primary" onClick={() => trackEvent('cta_essai_click', { location: 'hero' })}>
                <Button
                  size="lg"
                  className="btn-shine w-full sm:w-auto bg-primary hover:bg-primary-light text-white font-heading text-base tracking-wider px-8 py-7 rounded-xl glow-primary group"
                >
                  ESSAI GRATUIT
                  <ChevronRight className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/reservation" data-testid="hero-cta-reserve" onClick={() => trackEvent('reservation_click', { location: 'hero' })}>
                <Button
                  size="lg"
                  className="btn-shine w-full sm:w-auto bg-white hover:bg-white/90 text-primary font-heading text-base tracking-wider px-8 py-7 rounded-xl group shadow-[0_10px_30px_-10px_rgba(255,255,255,0.35)] ring-1 ring-white/20"
                >
                  <CalendarCheck className="mr-2 h-5 w-5" strokeWidth={2.4} />
                  RÉSERVER MA PLACE
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
              className="mb-10"
            >
              <Link
                to="/planning"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm tracking-wider transition-colors"
              >
                <Play className="h-3.5 w-3.5 fill-white/70" />
                <span className="link-underline">Voir le planning de la semaine</span>
              </Link>
            </motion.div>

            {/* Inline stats */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="flex flex-wrap gap-6 md:gap-10 pt-6 border-t border-white/10"
            >
              {[
                { val: '150+', label: 'Membres' },
                { val: '15 ans', label: 'Expérience' },
                { val: '5★', label: 'Google' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col">
                  <span className="font-display text-3xl md:text-4xl text-white leading-none">
                    {s.val}
                  </span>
                  <span className="text-white/50 text-[11px] tracking-[0.2em] uppercase mt-1.5">
                    {s.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* ===== RIGHT: Portrait image ===== */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
            style={{ y: imgY }}
            className="lg:col-span-5 order-1 lg:order-2 relative"
          >
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              {/* Main image */}
              <div className="relative h-full rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]">
                <img decoding="async"
                  src={heroPortrait.image_url}
                  alt={heroPortrait.alt}
                  className="w-full h-full object-cover ken-burns"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/60 via-transparent to-transparent" />
              </div>

              {/* Floating badge top-right */}
              <motion.div
                initial={{ opacity: 0, x: 20, y: -10 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                className="absolute -top-4 -right-3 md:-right-6 bg-primary text-white rounded-2xl px-4 py-3 shadow-[0_20px_40px_-10px_hsla(155,75%,18%,0.6)] rotate-3 animate-float-y"
              >
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  <span className="font-heading text-xs tracking-wider">
                    COACH CERTIFIÉ
                  </span>
                </div>
              </motion.div>

              {/* Floating stat bottom-left */}
              <motion.div
                initial={{ opacity: 0, x: -20, y: 10 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.8, delay: 1, ease: EASE }}
                className="absolute -bottom-5 -left-3 md:-left-6 bg-white rounded-2xl px-5 py-4 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] -rotate-2"
              >
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-3.5 w-3.5 fill-primary text-primary"
                      />
                    ))}
                  </div>
                  <div>
                    <div className="font-heading text-sm text-dark leading-none">
                      150+ membres
                    </div>
                    <div className="text-muted-foreground text-[10px] tracking-wider uppercase mt-0.5">
                      Satisfaits
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative ring */}
              <div className="absolute -z-10 -inset-4 rounded-[2rem] border border-primary/20 hidden md:block" />
              <div className="absolute -z-10 -inset-8 rounded-[2.5rem] border border-primary/10 hidden lg:block" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
